import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { findUnitPreset, formatQuantity } from '@abastece/shared';
import type { Existencia, Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { emitirYVer } from '../lib/emitir.js';
import {
  borradorDe,
  cambiosDe,
  existenciaQueCambiaDeUnidad,
  minimoInvalido,
  otroConEseNombre,
  type BorradorProducto,
} from '../lib/editar-producto.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { nombreEnIngles } from '../i18n/productos.js';
import { Campo, Interruptor, Selector } from './Campo.js';
import { BASES } from './FormularioProducto.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { CampoCategoria } from './CampoCategoria.js';

/** A unit's name as the screen says it, for one no preset knows too. */
const nombreDeUnidad = (codigo: string) => etiquetaUnidad(codigo, findUnitPreset(codigo)?.label ?? codigo);

/**
 * «Editar producto»: whoever may edit products changes any of the product's own properties in one
 * sheet, and «Guardar cambios» records what changed as one `producto.actualizado` (the owner,
 * 2026-10-04). The fields are the ones «Nuevo producto» asks, with the same choices, and the
 * minimum. The photo, the presentations, the prices and the codes keep their own controls on the
 * page, and so do the English name and the minimum, which are saved there on leaving them.
 *
 * A name another product on sale goes by is said under it and not saved (`otroConEseNombre`).
 * Another unit renames the stock and does not convert it (`reduceProducto`), which the line under
 * the unit says as soon as it is chosen, with the figure, when there is stock to rename.
 */
export function EditarProducto({
  producto,
  existencias,
  onCerrar,
}: {
  producto: Producto;
  /** The product's stock, in every place: what the line under the unit counts. */
  existencias: readonly Existencia[];
  onCerrar: () => void;
}) {
  const t = useT();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  /**
   * The product as the sheet opened on it, which a save is told apart from: only what was changed
   * here is sent. Held against the live one instead, a minimum another phone changed meanwhile, or
   * an English name saved on the page a moment before, would look like an edit and be put back.
   */
  const [abierto] = useState(producto);
  const [borrador, setBorrador] = useState<BorradorProducto>(() => borradorDe(producto));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const poner = (cambio: Partial<BorradorProducto>) => setBorrador((b) => ({ ...b, ...cambio }));

  const sinNombre = !borrador.nombre.trim();
  const repetido = otroConEseNombre(productos, producto, borrador.nombre);
  const malMinimo = minimoInvalido(borrador.stockMinimo);
  const bloqueado = sinNombre || Boolean(repetido) || malMinimo;
  const renombrada = existenciaQueCambiaDeUnidad(producto, borrador, existencias);
  // As on the page: the starter catalogue's English, until somebody types their own.
  const deLaSemilla = producto.nombreEn ? null : nombreEnIngles(producto);
  // Its own unit among them even when no new product is offered it any more, so it opens as it is.
  const unidades = [...new Set([...BASES.map((u) => u.code), producto.unidadBase])];

  async function guardar() {
    if (bloqueado || guardando) return;
    const cambios = cambiosDe(abierto, borrador);
    if (Object.keys(cambios).length === 0) {
      onCerrar();
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('producto.actualizado', { productoId: producto.id, ...cambios });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('producto.noSePudoGuardar'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      titulo={t('editarProducto.titulo')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={bloqueado || guardando} onClick={() => void guardar()}>
          <Ico icono="guardar" />
          {t(guardando ? 'comun.guardando' : 'editarProducto.guardar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4" data-editar-producto>
        <Campo
          etiqueta={t('producto.nombre')}
          value={borrador.nombre}
          onChange={(e) => poner({ nombre: e.target.value })}
          maxLength={120}
          autoComplete="off"
          error={sinNombre ? t('editarProducto.faltaNombre') : repetido ? t('editarProducto.yaExiste', { nombre: repetido.nombre }) : null}
        />
        <Campo
          etiqueta={t('producto.nombreEn')}
          value={borrador.nombreEn}
          placeholder={deLaSemilla ?? ''}
          onChange={(e) => poner({ nombreEn: e.target.value })}
          maxLength={120}
          autoComplete="off"
          lang="en"
          ayuda={t('producto.ayudaNombreEn')}
        />
        <CampoCategoria value={borrador.categoria} onChange={(categoria) => poner({ categoria })} />
        <Selector
          etiqueta={t('producto.unidadBase')}
          value={borrador.unidadBase}
          onChange={(e) => poner({ unidadBase: e.target.value })}
          ayuda={
            renombrada === null ? (
              t('editarProducto.ayudaUnidad')
            ) : (
              <span className="text-amber-300" data-solo-el-nombre>
                {t('editarProducto.soloElNombre', {
                  cantidad: formatQuantity(renombrada),
                  antes: nombreDeUnidad(producto.unidadBase),
                  despues: nombreDeUnidad(borrador.unidadBase),
                })}
              </span>
            )
          }
        >
          {unidades.map((codigo) => (
            <option key={codigo} value={codigo}>
              {nombreDeUnidad(codigo)}
            </option>
          ))}
        </Selector>
        <div>
          <Interruptor
            etiqueta={t('producto.esMedicamento')}
            descripcion={t('producto.descMedicamento')}
            checked={borrador.esMedicamento}
            onChange={(esMedicamento) => poner({ esMedicamento })}
          />
          <Interruptor
            etiqueta={t('producto.esPerecedero')}
            descripcion={t('producto.descPerecedero')}
            checked={borrador.perecedero}
            onChange={(perecedero) => poner({ perecedero })}
          />
          {borrador.esMedicamento && (
            <div className="mt-3">
              <Campo
                etiqueta={t('producto.registroSanitario')}
                value={borrador.registroSanitario}
                onChange={(e) => poner({ registroSanitario: e.target.value })}
                maxLength={60}
                autoComplete="off"
              />
            </div>
          )}
        </div>
        <Campo
          etiqueta={t('detalle.minimo', { unidad: nombreDeUnidad(borrador.unidadBase) })}
          value={borrador.stockMinimo}
          placeholder={t('detalle.minimoAutomatico')}
          onChange={(e) => poner({ stockMinimo: e.target.value })}
          inputMode="decimal"
          className="tabular"
          autoComplete="off"
          error={malMinimo ? t('detalle.minimoInvalido') : undefined}
          ayuda={t('detalle.ayudaMinimo')}
        />
        {error && <p className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>}
      </div>
    </Modal>
  );
}
